import { Button, FormControl, FormLabel, Input, InputGroup, InputRightElement, useToast } from "@chakra-ui/react";
import { Badge } from "flowbite-react";
import _, { get } from "lodash";
import { useEffect, useState } from "react";
import { BiHide, BiShow } from "react-icons/bi";
import { GrFormNextLink } from "react-icons/gr";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { MODULE_ASSESMENT_RESULTS } from "../../../../../consts/methods.consts";
import { MODULE_ASSESMENT_RESULT_TAGS } from "../../../../../consts/tags.consts";
import { useGetQuery, usePostMutation } from "../../../../../services/api.service";
import { ToastService } from "../../../../../services/toast.service";
import { showConfirmDialog } from "../../../../../store/reducers/confirm-modal";
import { LinkCell } from "../../../../common/chakra-table/cells";
import ChakraTable, { ChakraTableColumns } from "../../../../common/chakra-table/ChakraTable";
import { PageHeaderActions } from "../../../../common/page-header/PageHeader";
import { SectionCard } from "../../../../common/section-card/SectionCard";
import { useObjChange } from "../../../../hooks";

const AssessmentListTable = ({ parentData }: any) => {
	const [page, setPage] = useState(1)
	const [create, { isLoading: mutation }] = usePostMutation();

	const [modifiedData, setModifiedData] = useState([])
	const toast = new ToastService(useToast());

	const { data, isLoading, isFetching } = useGetQuery({
		method: MODULE_ASSESMENT_RESULTS.LIST,
		body: {
			page: page,
			program: _.get(parentData, 'program'),
			program_module: _.get(parentData, 'programModule')
		},
		providesTags: [MODULE_ASSESMENT_RESULT_TAGS.LIST],
	}, { refetchOnMountOrArgChange: true })


	let response = get(data, 'message', {})
	let isPO = get(response, 'is_po_role', false)
	let isPA = get(response, 'is_pa_role', false)
	let is_confirmed = get(response, 'is_confirmed', false)

	let dataList = get(response, 'data_list', [])
	const loading = isLoading || isFetching || mutation
	let isChanged = Array.isArray(modifiedData) && modifiedData.some((item: any) => item?.isChanged);


	useEffect(() => {
		setModifiedData(dataList)
	}, [JSON.stringify(dataList)])


	const bulkSaveHandler = async () => {
		try {
			const res = await create({
				method: MODULE_ASSESMENT_RESULTS.CREATE_BULK_ASSESSMENT_RESULTS,
				body: { data: modifiedData },
				invalidatesTags: [MODULE_ASSESMENT_RESULT_TAGS.LIST]
			}).unwrap();

			toast.setTitle("Success").setDescription("Module assessment result is updated successfully.").showSuccessToast();

		} catch (error: any) {
			let message = JSON.parse(_.get(error?.data, '_server_messages', {}))
			if (message) toast.setTitle("Error").setDescription(JSON.parse(message)?.message).showErrorToast();
		}
	}

	const confirmHandler = async () => {
		try {
			const res = await create({
				method: MODULE_ASSESMENT_RESULTS.CONFIRM_ASSESSMENT_RESULT,
				body: { pm: get(parentData, 'programModule') },
				invalidatesTags: [MODULE_ASSESMENT_RESULT_TAGS.LIST]
			}).unwrap();
			toast.setTitle("Success").setDescription("Module assessment result is confirmed successfully.").showSuccessToast();
		} catch (error: any) {
			let message = JSON.parse(_.get(error?.data, '_server_messages', {}))
			if (message) toast.setTitle("Error").setDescription(JSON.parse(message)?.message).showErrorToast();
		}

	}

	const revertHandler = async () => {
		try {
			const res = await create({
				method: MODULE_ASSESMENT_RESULTS.REVERT_ASSESSMENT_RESULT,
				body: { pm: get(parentData, 'programModule') },
				invalidatesTags: [MODULE_ASSESMENT_RESULT_TAGS.LIST]
			}).unwrap();
			toast.setTitle("Success").setDescription("Module assessment result is reverted to draft successfully.").showSuccessToast();
		} catch (error: any) {
			let message = JSON.parse(_.get(error?.data, '_server_messages', {}))
			if (message) toast.setTitle("Error").setDescription(JSON.parse(message)?.message).showErrorToast();
		}
	}

	const MODULE_RESULT_COLUMNS: ChakraTableColumns = [
		{
			header: "ID",
			accessor: "student",
			customRenderer(value: any = "", item: any) {
				const url = `/app/student/${value}`
				return (
					<div className="w-[150px]">
						<LinkCell className={"w-full"} navigateTo={url} value={value} />
					</div>
				)
			}
		},
		{
			header: "Full Name",
			accessor: "full_name",
		},
		{
			header: "NIC",
			accessor: "nic",
		},
		{
			header: "Status",
			accessor: "status",
			customRenderer(value: any = "", item: any) {
				const status = get(item, 'status', '')
				return (
					<Badge color={status == "Confirmed" ? "green" : "yellow"} className=" flex justify-center" >{status}</Badge>
				)
			}
		},
		{
			header: "Marks",
			accessor: "mark",
			customRenderer(value: any = "", item: any) {
				return <EditableField value={value} item={item} isPA={isPA} isPO={isPO} modifiedData={modifiedData} setModifiedData={setModifiedData} />
			}

		},
		...(isPO && !is_confirmed) ? [{
			header: "Actions",
			accessor: "_",
			customRenderer(value = "", item: any) {

				return <SingleSaveButton item={item} modifiedData={modifiedData} setModifiedData={setModifiedData} />
			},
		}] : []
	]


	const actions: PageHeaderActions = [
		...is_confirmed ? [] : [...(isPA || isPO) ? [{
			text: "Save",
			onClick: bulkSaveHandler,
			isDisabled: loading || !isChanged,
			buttonVariant: "solid",
			buttonColorScheme: "teal"
		}] : []],
		...isPO ? [{
			text: is_confirmed ? "Revert to Draft" : "Confirm Assessment Marks",
			onClick: is_confirmed ? revertHandler : confirmHandler,
			isDisabled: loading || isChanged,
			buttonVariant: "solid",
			buttonColorScheme: is_confirmed ? "red" : "teal"
		}] : []
	]

	return (
		<div>
			<SectionCard actions={actions} bg={'white'} subTitle="" title="Assessment Results" p={3}>
				<ChakraTable isFetching={loading} isLoading={isLoading} size={"sm"} header
					columns={MODULE_RESULT_COLUMNS} data={modifiedData} />
			</SectionCard>
		</div>
	)

}

export default AssessmentListTable

const EditableField = ({ value, item, isPA, isPO, modifiedData, setModifiedData }: any) => {

	const editable = !get(item, 'confirmed', false) && (isPA || isPO)
	const index = modifiedData?.findIndex((line: any) => line?.student === item?.student);
	const toast = new ToastService(useToast());

	if (index === -1) return <></>


	const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;

		if (inputValue === "") {
			const updatedData: any = [...modifiedData];
			updatedData[index] = { ...item, mark: "",isChanged: true };
			setModifiedData(updatedData);
			return
		}

		const mark: number = Number(inputValue);

		if (isNaN(mark) || mark < 0 || mark > 100) {
			toast.setTitle("warning")
				.setDescription("Mark should be between 0 and 100")
				.setStatus("warning").show()
			return
		}

		const updatedData: any = [...modifiedData];
		updatedData[index] = { ...item, mark, isChanged: true };
		setModifiedData(updatedData);
	};

	return (
		<FormInput
			width="150px"
			isDisabled={!editable}
			onChange={onChangeHandler}
			value={value ?? ""}
			type="number"
			isRequired
			name="mark"
		/>
	);
}


const FormInput = ({ rounded = "md", size = 'sm', type = 'text', onChange, name, label, value,isDisabled }: any) => {
	

	return (
		<FormControl >
			<InputGroup size='md'>
				<Input isDisabled={isDisabled} type={type} shadow={"sm"} borderColor={'gray.300'} size={size} name={name} value={value} onChange={onChange} rounded={rounded} />
			</InputGroup>
		</FormControl>
	)
}

const SingleSaveButton = ({ item, modifiedData, setModifiedData }: any) => {
	const [create, { isLoading: mutation }] = usePostMutation();
	const toast = new ToastService(useToast());
	const confirmed = get(item, 'confirmed', '')
	const isChanged = get(item, 'isChanged')
	const dispatch = useDispatch()
	const index:number = modifiedData?.findIndex((line: any) => line?.student === item?.student);

	const singleSaveHandler = async (item: any) => {

		dispatch(showConfirmDialog({
			title: 'Confirmation Required!',
			subtitle: 'Are you sure you want to continue the action?',
			onConfirm: async () => {
				try {
					const res = await create({
						method: MODULE_ASSESMENT_RESULTS.ADD_SINGLE_ASSESSMENT_RESULT,
						body: { data: item }
					}).unwrap();

					toast.setTitle("Success").setDescription("Module assessment result is updated successfully.").showSuccessToast();

					const updatedData: any = [...modifiedData];
					updatedData[index] = { ...item, isChanged: false };
					setModifiedData(updatedData);

				} catch (error: any) {
					let message = JSON.parse(_.get(error?.data, '_server_messages', {}))
					if (message) toast.setTitle("Error").setDescription(JSON.parse(message)?.message).showErrorToast();
				}
			}
		}))
	}

	return (
		<Button colorScheme="teal" isLoading={mutation} isDisabled={confirmed || !isChanged} onClick={() => singleSaveHandler(item)} size={'sm'}>Save</Button>
	)
}